import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Flame } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

// Fallback: next late-April Saturday (rough Wisconsin prom season).
function fallbackPromDate() {
  const now = new Date();
  const y = now.getUTCFullYear();
  const thisYear = new Date(`${y}-04-25T18:00:00-05:00`);
  if (thisYear.getTime() > now.getTime()) return thisYear;
  return new Date(`${y + 1}-04-24T18:00:00-05:00`);
}

function parseSchoolProm(iso) {
  if (!iso || typeof iso !== "string") return null;
  // Accept "YYYY-MM-DD" or full ISO. Assume 6pm CT on that day.
  const short = /^\d{4}-\d{2}-\d{2}$/.test(iso);
  const d = new Date(short ? `${iso}T18:00:00-05:00` : iso);
  return isNaN(d.getTime()) ? null : d;
}

function diffFrom(target) {
  const ms = target.getTime() - Date.now();
  const days = Math.max(0, Math.floor(ms / 86400000));
  const hours = Math.max(0, Math.floor((ms / 3600000) % 24));
  const mins = Math.max(0, Math.floor((ms / 60000) % 60));
  return { days, hours, mins, past: ms <= 0 };
}

export default function PromCountdown() {
  const { user } = useAuth();
  const [schoolProm, setSchoolProm] = useState(null);
  const [schoolName, setSchoolName] = useState("");

  // If the signed-in user has a school, pull that school's confirmed prom date.
  // Falls back to a generic late-April Saturday when we don't have one.
  useEffect(() => {
    if (!user?.school_id) { setSchoolProm(null); setSchoolName(""); return; }
    let cancelled = false;
    api.school(user.school_id).then(({ data }) => {
      if (cancelled) return;
      setSchoolProm(parseSchoolProm(data?.prom_date));
      setSchoolName(data?.name || "");
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user?.school_id]);

  const target = useMemo(() => schoolProm || fallbackPromDate(), [schoolProm]);
  const [t, setT] = useState(() => diffFrom(target));
  useEffect(() => {
    setT(diffFrom(target));
    const id = setInterval(() => setT(diffFrom(target)), 30000);
    return () => clearInterval(id);
  }, [target]);

  if (t.past) return null;

  const urgent = t.days < 30;
  const veryUrgent = t.days < 14;
  const personalised = !!schoolProm;
  const seasonYear = target.getFullYear();

  const wrapper = urgent
    ? "block relative overflow-hidden bg-gradient-to-r from-[#3a0f14] via-[#5c1a22] to-[#3a0f14] text-cream group"
    : "block relative overflow-hidden bg-gradient-to-r from-ink via-[#1a0e0f] to-ink text-cream group";

  return (
    <Link to="/prom-dresses" data-testid="prom-countdown" data-personalised={personalised ? "1" : "0"} className={wrapper}>
      <div className="absolute inset-0 opacity-40" style={{
        background: urgent
          ? "radial-gradient(500px 100px at 20% 50%, rgba(220,60,90,0.55), transparent)"
          : "radial-gradient(500px 100px at 20% 50%, rgba(230,196,143,0.35), transparent)",
      }} />
      <div className="absolute inset-0 opacity-40" style={{
        background: urgent
          ? "radial-gradient(500px 100px at 80% 50%, rgba(183,110,121,0.65), transparent)"
          : "radial-gradient(500px 100px at 80% 50%, rgba(183,110,121,0.35), transparent)",
      }} />
      {veryUrgent && (
        <div className="absolute inset-0 opacity-60 animate-pulse" style={{
          background: "linear-gradient(90deg, transparent, rgba(255,80,110,0.25), transparent)",
        }} />
      )}
      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 py-2.5 flex flex-wrap items-center justify-center gap-3 text-center">
        <span className={`inline-flex items-center gap-1.5 font-label text-[9px] tracking-[0.25em] ${urgent ? "text-[#ffb0be]" : "text-champagne"}`}>
          {urgent ? <Flame className="w-3 h-3" strokeWidth={1.75} /> : <Sparkles className="w-3 h-3" strokeWidth={1.5} />}
          {personalised
            ? `${schoolName.replace(/\s+High School$/i, "").toUpperCase()} PROM ${seasonYear}${urgent ? (veryUrgent ? " · FINAL DAYS" : " · URGENT") : ""}`
            : (urgent ? `${veryUrgent ? "FINAL DAYS" : "URGENT"} · PROM ${seasonYear}` : `PROM ${seasonYear} SEASON`)}
        </span>
        <span className={`font-display italic text-lg leading-none ${urgent ? "text-[#ff8fa3]" : "text-champagne"}`}>
          {t.days}
          <span className="font-label text-[9px] tracking-widest ml-1">DAYS</span>
          {t.days < 60 && (
            <>
              &nbsp;·&nbsp;{String(t.hours).padStart(2, "0")}<span className="font-label text-[9px] tracking-widest">H</span>
              &nbsp;{String(t.mins).padStart(2, "0")}<span className="font-label text-[9px] tracking-widest">M</span>
            </>
          )}
        </span>
        <span className={`font-label text-[10px] tracking-widest hidden sm:inline ${urgent ? "text-cream" : "text-cream/70"}`}>
          {urgent ? "LOCK YOUR LOOK BEFORE SHE DOES" : "TO LOCK YOUR LOOK"}
        </span>
        <span className={`inline-flex items-center gap-1 font-label text-[10px] tracking-widest transition-colors ${urgent ? "text-[#ffb0be] group-hover:text-cream" : "text-champagne group-hover:text-cream"}`}>
          {urgent ? "SHOP NOW →" : "SHOP THE PROM EDIT →"}
        </span>
      </div>
    </Link>
  );
}
